import { Tenant, TenantSortBy, TenantsHook } from "@models";
import { useStore } from "@stores";
import { useDebouncedCallback } from "use-debounce";
import { useCallback, useEffect, useState } from "react";

interface Options {
  /** Grad iz URL-a; `undefined` znači cela Srbija. */
  townId?: number;
  /**
   * Odloži prvo učitavanje dok se filter ne zna (dok se grad iz URL-a ne
   * razreši). Bez toga prvi zahtev ode bez grada, pa ga drugi odmah zameni —
   * dupli poziv i vidljiv skok sadržaja.
   */
  enabled?: boolean;
  /**
   * Dozvoli upit bez kategorije (svi saloni, npr. u gradu). Podrazumevano je
   * kategorija obavezna, pa lista bez nje ostaje prazna, a ne "cela baza".
   */
  anyCategory?: boolean;
}

export const useTenants = (
  category?: string,
  { townId, enabled = true, anyCategory = false }: Options = {},
): TenantsHook => {
  const { tenantStore } = useStore();
  const {
    loading,
    loadingMore,
    loadFailed,
    loadedOnce,
    loadedCount,
    tenants,
    totalCount,
    maxResultCount,
  } = tenantStore;

  const [sortBy, setSortBy] = useState<TenantSortBy>("Recommended");
  const [searchValue, setSearchValue] = useState("");
  const [keyword, setKeyword] = useState<string | undefined>(undefined);

  // Pretraga važi samo za tekuću kategoriju. Reset se radi u renderu, a ne u
  // efektu, da bi efekat koji učitava listu odmah video prazan pojam — inače
  // bi novu kategoriju prvo dovukao filtriranu pa je odmah ponovo tražio.
  const [renderedCategory, setRenderedCategory] = useState(category);
  if (category !== renderedCategory) {
    setRenderedCategory(category);
    setSearchValue("");
    setKeyword(undefined);
  }

  const debouncedSetKeyword = useDebouncedCallback((value: string) => {
    setKeyword(value || undefined);
  }, 400);

  const onSearch = (value: string) => {
    setSearchValue(value);
    debouncedSetKeyword(value);
  };

  // Nova kategorija je nova stranica, pa stara lista mora odmah da nestane.
  // Promena grada ili pretrage se namerno NE čisti: `getAll` zameni stavke u
  // jednoj akciji, pa lista ostaje na ekranu dok nova ne stigne umesto da
  // trepne u prazno.
  useEffect(() => {
    tenantStore.clearTenants();
    return () => {
      debouncedSetKeyword.cancel();
      tenantStore.clearTenants();
    };
  }, [tenantStore, category, debouncedSetKeyword]);

  useEffect(() => {
    if ((!category && !anyCategory) || !enabled) return;
    tenantStore.getAll({ category, maxResultCount, skipCount: 0, keyword, townId, sortBy });
  }, [tenantStore, category, anyCategory, enabled, maxResultCount, keyword, townId, sortBy]);

  const dataSource = Array.from(tenants.values()) as Tenant[];

  const loadMore = useCallback(() => {
    if ((!category && !anyCategory) || tenantStore.loading || tenantStore.loadingMore) return;
    tenantStore.getAll(
      { category, maxResultCount, skipCount: tenantStore.loadedCount, keyword, townId, sortBy },
      true,
    );
  }, [tenantStore, category, anyCategory, maxResultCount, keyword, townId, sortBy]);

  return {
    dataSource,
    loading,
    loaded: loadedOnce && !loading,
    loadingMore,
    hasMore: loadedCount < totalCount,
    loadMore,
    totalCount,
    failed: loadFailed,
    filtered: !!keyword,
    searchValue,
    onSearch,
    sortBy,
    onSortChange: setSortBy,
  };
};
